import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ShieldCheck, ShieldAlert, ShieldX, Eye, Clock } from 'lucide-react';
import { StartReviewButton } from './StartReviewButton';
import { gateDecisionLabel } from '@/lib/enumLabels';
import { cn } from '@/lib/utils';
import type { GateDecision, GateReport, Job } from '@/types';

interface GateReportViewerProps {
  job: Job;
  report: GateReport;
  onReviewClaimed?: () => void;
}

type DecisionConfig = {
  label: string;
  bannerClass: string;
  badgeClass: string;
  Icon: React.FC<{ className?: string }>;
  description: string;
};

const DECISION_CONFIG: Record<GateDecision, DecisionConfig> = {
  APPROVED: {
    label: 'AI Recommendation: Contract Approved',
    bannerClass: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    Icon: ShieldCheck,
    description: 'AI recommends approval. Human review is still required before this contract can become active.',
  },
  APPROVED_WITH_ALERTS: {
    label: 'AI Recommendation: Approved with Alerts',
    bannerClass: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    Icon: ShieldAlert,
    description: 'AI recommends approval but flagged alerts that should be reviewed before final decision.',
  },
  REJECTED: {
    label: 'AI Recommendation: Contract Rejected',
    bannerClass: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    Icon: ShieldX,
    description: 'This contract requires human review before a final decision can be made.',
  },
  ESCALATED: {
    label: 'AI Recommendation: Manual Review Required',
    bannerClass: 'bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800',
    badgeClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    Icon: Eye,
    description: 'The AI requires human judgment to make a final decision on this contract.',
  },
  MANUAL_REVIEW_REQUIRED: {
    label: 'AI Recommendation: Manual Review Required',
    bannerClass: 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800',
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    Icon: Clock,
    description: 'The AI could not reach a decision — this contract requires mandatory human review.',
  },
};

function HardIssueSummary({ count }: { count: number }) {
  if (count === 0) return <span className="text-green-700 dark:text-green-400">No blocking issues</span>;
  return <span className="text-destructive font-medium">{count} blocking issue{count !== 1 ? 's' : ''}</span>;
}

function AlertSummary({ count }: { count: number }) {
  if (count === 0) return <span className="text-muted-foreground">No alerts</span>;
  return <span className="text-amber-700 dark:text-amber-400 font-medium">{count} alert{count !== 1 ? 's' : ''}</span>;
}

export function GateReportViewer({ job, report, onReviewClaimed }: GateReportViewerProps) {
  // Defensive lookup: report.decision is typed as GateDecision but the AI
  // pipeline could write an unexpected value; fall back to manual review.
  const decisionKey = report.decision;
  const config =
    (DECISION_CONFIG as Record<string, DecisionConfig | undefined>)[decisionKey] ??
    DECISION_CONFIG.MANUAL_REVIEW_REQUIRED;
  const { Icon } = config;

  const isClaimed = job.review_status === 'claimed' || job.review_status === 'in_progress';
  const isReviewed = job.review_status === 'reviewed';

  return (
    <section aria-labelledby="gate-report-heading" className="space-y-4">
      <h2 id="gate-report-heading" className="text-lg font-semibold">Gate Report</h2>

      {/* AI Recommendation Banner */}
      <div
        className={cn('rounded-lg border p-4 flex items-start gap-3', config.bannerClass)}
        role="region"
        aria-label="AI Recommendation"
      >
        <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{config.label}</p>
          <p className="text-sm mt-0.5 text-muted-foreground">{config.description}</p>
        </div>
        <Badge className={cn('text-xs flex-shrink-0', config.badgeClass)}>
          {(gateDecisionLabel as Record<string, string | undefined>)[decisionKey] ??
            gateDecisionLabel.MANUAL_REVIEW_REQUIRED}
        </Badge>
      </div>

      {/* Human Review Status */}
      <div className="flex items-center gap-2 flex-wrap">
        {isReviewed ? (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800">
            Review Complete
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800">
            <Clock className="h-3 w-3 mr-1" aria-hidden="true" />
            Awaiting Human Review
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">
          Per FR-077: every gate outcome requires explicit human approval before activation.
        </span>
      </div>

      <Separator />

      {/* Findings Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Findings Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground text-xs uppercase tracking-wide">Blocking Issues</span>
              <div className="mt-1"><HardIssueSummary count={report.blocking_issues.length} /></div>
            </div>
            <div>
              <span className="text-muted-foreground text-xs uppercase tracking-wide">Alerts</span>
              <div className="mt-1"><AlertSummary count={report.alerts.length} /></div>
            </div>
          </div>
          {report.summary && (
            <p className="mt-3 text-sm text-muted-foreground border-t pt-3">{report.summary}</p>
          )}
        </CardContent>
      </Card>

      {/* Start Review CTA — FR-077: all decisions */}
      {!isReviewed && (
        <StartReviewButton
          jobId={job.id}
          isClaimed={isClaimed}
          onClaimed={onReviewClaimed}
        />
      )}
    </section>
  );
}
